import React, { useState, useEffect, useRef } from 'react';
import { X, Upload, XCircle } from 'lucide-react';
import { Button } from '../shared';
import ModalShell from '../shared/ModalShell';
import { useToastContext } from '../../contexts/ToastContext';

const EventStartModal = ({ isOpen, onClose, onStart, isLoading }) => {
  const { showToast } = useToastContext();
  const [notes, setNotes] = useState('');
  const [screenshots, setScreenshots] = useState([]);
  const [previews, setPreviews] = useState([]);
  const onCloseRef = useRef(onClose);

  // Keep onClose ref updated
  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  const handleClose = () => {
    setNotes('');
    setScreenshots([]);
    setPreviews([]);
    onCloseRef.current();
  };

  // Reset form when modal closes
  useEffect(() => {
    if (!isOpen) {
      setNotes('');
      setScreenshots([]);
      setPreviews([]);
    }
  }, [isOpen]);

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    const validFiles = files.filter(file => {
      const isValidType = ['image/jpeg', 'image/png', 'image/jpg', 'image/gif'].includes(file.type);
      const isValidSize = file.size <= 5 * 1024 * 1024; // 5MB
      return isValidType && isValidSize;
    });

    if (validFiles.length !== files.length) {
      showToast('Some files were rejected. Only JPEG, PNG, JPG, GIF images up to 5MB are allowed.', 'warning');
    }

    // Limit to 5 total screenshots
    const totalScreenshots = screenshots.length + validFiles.length;
    if (totalScreenshots > 5) {
      showToast('Maximum 5 screenshots allowed. Only the first 5 will be uploaded.', 'warning');
      const remainingSlots = 5 - screenshots.length;
      validFiles.splice(remainingSlots);
    }

    setScreenshots([...screenshots, ...validFiles]);

    // Create previews
    validFiles.forEach(file => {
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreviews(prev => [...prev, { file, url: reader.result }]);
      };
      reader.readAsDataURL(file);
    });
  };

  const removeScreenshot = (index) => {
    setScreenshots(screenshots.filter((_, i) => i !== index));
    setPreviews(previews.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate at least 1 screenshot is required
    if (!screenshots || screenshots.length === 0) {
      showToast('Please upload at least 1 screenshot', 'error');
      return;
    }
    
    try {
      // Call onStart and wait for it to complete if it returns a promise
      const result = onStart({
        notes: notes.trim() || null,
        screenshots: screenshots,
      });
      
      // If onStart returns a promise, wait for it
      if (result && typeof result.then === 'function') {
        await result;
      }
      
      // Close modal after successful submission (if not already closed by parent)
      // The parent component will handle closing via useEffect
    } catch (error) {
      console.error('Error starting event:', error);
      showToast(
        error?.response?.data?.message || error?.message || 'Could not start event',
        'error'
      );
    }
  };

  return (
    <ModalShell
      isOpen={isOpen}
      onClose={handleClose}
      labelledBy="event-start-title"
      panelClassName="max-w-2xl"
    >
      <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-700 shadow-xl overflow-hidden">
        {/* Header */}
        <div className="sticky top-0 bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-800 px-6 py-4 flex items-center justify-between z-10">
          <h2 id="event-start-title" className="text-xl font-bold text-gray-900 dark:text-gray-50">
            Start Event
          </h2>
          <button
            type="button"
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors p-1 !rounded-xl hover:bg-gray-100 dark:hover:bg-gray-800"
            aria-label="Close"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Notes (Optional)
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={4}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-950 text-gray-900 dark:text-gray-100 rounded-xl focus:ring-2 focus:ring-primary-500/40 focus:border-primary-500"
              placeholder="Add any notes about starting the event..."
              maxLength={2000}
            />
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              {notes.length}/2000 characters
            </p>
          </div>

          {/* Screenshots */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Screenshots <span className="text-red-500">*</span>
            </label>
            <div className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-xl p-6">
              <input
                type="file"
                id="screenshots"
                multiple
                accept="image/jpeg,image/png,image/jpg,image/gif"
                onChange={handleFileChange}
                className="hidden"
              />
              <label
                htmlFor="screenshots"
                className="flex flex-col items-center justify-center cursor-pointer"
              >
                <Upload className="w-10 h-10 text-gray-400 mb-2" />
                <p className="text-sm text-gray-600 dark:text-gray-300 mb-1">
                  Click to upload screenshots
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  JPEG, PNG, JPG, GIF up to 5MB each (1-5 images required)
                </p>
              </label>
            </div>

            {/* Preview Grid */}
            {previews.length > 0 && (
              <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 gap-4">
                {previews.map((preview, index) => (
                  <div key={index} className="relative group">
                    <img
                      src={preview.url}
                      alt={`Screenshot ${index + 1}`}
                      className="w-full h-32 object-cover rounded-xl border border-gray-200 dark:border-gray-700"
                    />
                    <button
                      type="button"
                      onClick={() => removeScreenshot(index)}
                      className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <XCircle className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-200 dark:border-gray-800">
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={isLoading}
              className="!rounded-xl"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="primary"
              loading={isLoading}
              disabled={isLoading}
              className="!rounded-xl"
            >
              Start Event
            </Button>
          </div>
        </form>
      </div>
    </ModalShell>
  );
};

export default EventStartModal;
